import { EditOutlined } from '@ant-design/icons';
import { useMemoizedFn } from 'ahooks';
import { AutoComplete, Form, Input, Switch, Tooltip, Collapse } from 'antd';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import classNames from 'classnames';
import { CarrierTypeEnum } from 'domains/shippingDomain';
import { useOrgId } from 'hooks/useOrg';
import { isEmpty } from 'lodash-es';
import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import states from 'states-us/dist';
import { RULE_INTEGER, RULE_TEXT_LEN_255 } from 'utils/validators';
import AddressValidation, { ValidationRef } from './AddressValidation';
import {
  ExpansionStateEnum,
  OrgShippingCarrier,
  OrgShippingCarrierUps,
} from 'domains/orgDomain';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { RevxButton } from 'components/RevxButton';

const { OPEN, CLOSED } = ExpansionStateEnum;

const COLLAPSE_OPEN_KEY = '1';

export const allStatesOptions = states.map((s) => {
  return {
    value: s.abbreviation,
    label: `${s.abbreviation} (${s.name})`,
  };
});

const CarriersFormUPS = () => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [saveLoading, setSaveLoading] = useState<boolean>(false);

  const carrierUps = useAppSelector((state) => {
    return state.shipping.orgShippingConfig?.carriers?.find(
      (c) => c.carrierType === CarrierTypeEnum.UPS
    );
  });
  const ups = carrierUps?.ups;

  useEffect(() => {
    if (ups) {
      allowBtnDisabledHandler(ups);
    }
  }, [ups]);

  const loading = useAppSelector(
    (state) =>
      state.loading.effects.shipping.getOrgShippingConfig ||
      state.loading.effects.shipping.saveOrgShippingCarrier
  );
  const dispatch = useAppDispatch();
  const orgId = useOrgId();

  const [editing, setEditing] = useState<boolean>(false);
  const [allowBtnDisabled, setAllowBtnDisabled] = useState(true);
  const openProp = editing ? {} : { open: false };
  const [countryCode, setCountryCode] = useState(
    ups?.payer?.address?.countryCode
  );

  const [stateOptions, setStateOptions] = useState(allStatesOptions);
  const addrValidationRef = useRef<ValidationRef>(null);

  const onSearchStates = (searchText: string) => {
    if (countryCode === 'US') {
      if (!searchText) {
        setStateOptions(allStatesOptions);
      } else {
        setStateOptions(
          allStatesOptions.filter((o) => {
            if (searchText.length === 1) {
              return o.value.toLowerCase().startsWith(searchText.toLowerCase());
            } else if (searchText.length === 2) {
              return o.value.toLowerCase() === searchText.toLowerCase();
            }
            return o.label.toLowerCase().includes(searchText.toLowerCase());
          })
        );
      }
    }
  };
  const onSave = useMemoizedFn(async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch (validationError) {
      return;
    }
    setSaveLoading(true);
    const shouldSkipAddrValidation =
      isEmpty(values.payer.address.addressLine) ||
      isEmpty(values.payer.address.city) ||
      isEmpty(values.payer.address.stateProvinceCode) ||
      isEmpty(values.payer.address.countryCode) ||
      isEmpty(values.payer.address.postalCode);
    const addrValidationResult =
      !shouldSkipAddrValidation &&
      (await addrValidationRef.current?.validate({
        addressLine: values.payer.address.addressLine,
        city: values.payer.address.city,
        stateProvinceCode: values.payer.address.stateProvinceCode,
        countryCode: values.payer.address.countryCode,
        postalCode: values.payer.address.postalCode,
      }));

    if (shouldSkipAddrValidation || addrValidationResult) {
      await updateCarrierHandler(values);
      setEditing(false);
    }
    setSaveLoading(false);
  });

  const updateCarrierHandler = async (
    values: Partial<OrgShippingCarrier | OrgShippingCarrierUps> & {
      allowed?: boolean;
    }
  ) => {
    const { allowed } = values;
    try {
      await dispatch.shipping.saveOrgShippingCarrier({
        orgId,
        ...({
          ...carrierUps,
          ...{
            allowed: allowed !== undefined ? allowed : carrierUps?.allowed,
          },
          ...{
            ups: {
              ...ups,
              ...values,
            },
          },
        } as OrgShippingCarrier),
      });
    } finally {
      setSaveLoading(false);
    }
  };

  const onCollapseChange = async (keys: string | string[]) => {
    await updateCarrierHandler({
      expansionState: keys.length > 1 ? OPEN : CLOSED,
    });
  };

  const allowBtnDisabledHandler = (payload: OrgShippingCarrierUps) => {
    const { accountNumber, freightEnabled, packageEnabled, payer } = payload;
    setAllowBtnDisabled(
      !accountNumber ||
        !payer?.address?.countryCode ||
        !freightEnabled ||
        !packageEnabled
    );
  };

  const orgCarrierAllow = async (checked: boolean) => {
    form.setFieldsValue({ allowed: checked });
  };

  return (
    <Collapse
      defaultActiveKey={ups?.expansionState === OPEN ? COLLAPSE_OPEN_KEY : 0}
      onChange={onCollapseChange}
    >
      <Collapse.Panel key={COLLAPSE_OPEN_KEY} header="UPS">
        <RevxFlexLayout className="mb-40 allow-tips" justify="space-between">
          <RevxFlexLayout>
            <div className="mr-30">{`Allow ${carrierUps?.carrierType} for this organization?`}</div>
            <Switch
              disabled={!editing || allowBtnDisabled}
              onChange={orgCarrierAllow}
              defaultChecked={carrierUps?.allowed}
            />
          </RevxFlexLayout>
          {!editing && (
            <Tooltip title={t('common.edit')}>
              <EditOutlined
                onClick={() => {
                  setEditing(true);
                }}
              />
            </Tooltip>
          )}
        </RevxFlexLayout>
        <Form
          labelCol={{ span: 8 }}
          form={form}
          wrapperCol={{ span: 16 }}
          layout={'horizontal'}
          initialValues={{
            ...ups,
            allowed: carrierUps?.allowed,
          }}
          onValuesChange={(changedValues, allValues) => {
            allowBtnDisabledHandler(allValues);
          }}
          autoComplete="off"
        >
          <Form.Item name="allowed" hidden={true} />
          <Form.Item
            label={t('platform.shipping.form.clientId')}
            name="clientId"
            rules={[RULE_TEXT_LEN_255, { required: true }]}
          >
            <Input
              readOnly={!editing}
              className={classNames({
                'revx-input-no-border': !editing,
              })}
              autoComplete="off"
            />
          </Form.Item>
          <Form.Item
            label={t('platform.shipping.form.clientSecret')}
            name="clientSecret"
            rules={[RULE_TEXT_LEN_255, { required: true }]}
          >
            <Input
              readOnly={!editing}
              className={classNames({
                'revx-input-no-border': !editing,
              })}
              autoComplete="off"
            />
          </Form.Item>
          <Form.Item
            label={t('platform.shipping.form.accountNumber')}
            name="accountNumber"
            rules={[RULE_TEXT_LEN_255, { required: true }]}
          >
            <Input
              readOnly={!editing}
              className={classNames({
                'revx-input-no-border': !editing,
              })}
              autoComplete="off"
            />
          </Form.Item>
          <Form.Item
            label={t('platform.shipping.form.freightEnabled')}
            name="freightEnabled"
            valuePropName="checked"
          >
            <Switch disabled={!editing} />
          </Form.Item>
          <Form.Item
            label={t('platform.shipping.form.packageEnabled')}
            name="packageEnabled"
            valuePropName="checked"
          >
            <Switch disabled={!editing} />
          </Form.Item>
          <Form.Item
            label={t('platform.shipping.form.markup')}
            name="markupPct"
            rules={[RULE_INTEGER, { required: false }]}
          >
            <Input
              readOnly={!editing}
              prefix={'%'}
              autoComplete="off"
              className={classNames({
                'revx-input-no-border': !editing,
              })}
            />
          </Form.Item>
          <Form.Item label={t('platform.shipping.form.payer')} />
          <Form.Item
            label={t('platform.shipping.form.name')}
            name={['payer', 'name']}
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input
              readOnly={!editing}
              autoComplete="off"
              className={classNames({
                'revx-input-no-border': !editing,
              })}
            />
          </Form.Item>
          <Form.Item
            label={t('platform.shipping.form.shipperNumber')}
            name={['payer', 'shipperNumber']}
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input
              readOnly={!editing}
              autoComplete="off"
              className={classNames({
                'revx-input-no-border': !editing,
              })}
            />
          </Form.Item>
          <AddressValidation
            address={{
              addressLine: form.getFieldValue([
                'payer',
                'address',
                'addressLine',
              ]),
              city: form.getFieldValue(['payer', 'address', 'countryCode']),
              stateProvinceCode: form.getFieldValue([
                'payer',
                'address',
                'stateProvinceCode',
              ]),
              countryCode: form.getFieldValue([
                'payer',
                'address',
                'countryCode',
              ]),
              postalCode: form.getFieldValue([
                'payer',
                'address',
                'postalCode',
              ]),
            }}
            onApply={(record) => {
              form.setFieldsValue({
                payer: {
                  address: {
                    addressLine: record.addressLine,
                    city: record.city,
                    stateProvinceCode: record.stateProvinceCode,
                    countryCode: record.countryCode,
                    postalCode: record.postalCode,
                  },
                },
              });
            }}
            ref={addrValidationRef}
          />
          <Form.Item
            label={t('platform.shipping.form.countryCode')}
            name={['payer', 'address', 'countryCode']}
            rules={[RULE_TEXT_LEN_255, { required: true }]}
          >
            <AutoComplete
              onChange={(val) => {
                setCountryCode(val);
                if (val === 'US') {
                  setStateOptions(allStatesOptions);
                } else {
                  setStateOptions([]);
                }
              }}
              options={[
                { label: 'US', value: 'US' },
                { label: 'CA', value: 'CA' },
              ]}
              {...openProp}
              className={classNames({
                'revx-input-no-border': !editing,
              })}
            />
          </Form.Item>
          <Form.Item
            label={t('platform.shipping.form.stateProvinceCode')}
            name={['payer', 'address', 'stateProvinceCode']}
          >
            <AutoComplete
              {...openProp}
              onSearch={onSearchStates}
              options={stateOptions}
              className={classNames({
                'revx-input-no-border': !editing,
              })}
            />
          </Form.Item>
          <Form.Item
            label={t('platform.shipping.form.city')}
            name={['payer', 'address', 'city']}
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input
              readOnly={!editing}
              autoComplete="off"
              className={classNames({
                'revx-input-no-border': !editing,
              })}
            />
          </Form.Item>
          <Form.Item
            label={t('platform.shipping.form.addressLine')}
            name={['payer', 'address', 'addressLine']}
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input
              readOnly={!editing}
              autoComplete="off"
              className={classNames({
                'revx-input-no-border': !editing,
              })}
            />
          </Form.Item>

          <Form.Item
            label={t('platform.shipping.form.postalCode')}
            name={['payer', 'address', 'postalCode']}
            rules={[RULE_TEXT_LEN_255]}
          >
            <Input
              readOnly={!editing}
              autoComplete="off"
              className={classNames({
                'revx-input-no-border': !editing,
              })}
            />
          </Form.Item>
        </Form>
        {editing && (
          <RevxFlexLayout justify="justify-end">
            <RevxButton
              onClick={() => {
                setEditing(false);
                form.setFieldsValue({ ...ups });
              }}
            >
              {t('common.cancel')}
            </RevxButton>
            <RevxButton
              type="primary"
              className="ml-10"
              loading={saveLoading}
              onClick={() => {
                if (!loading && !saveLoading) {
                  onSave();
                }
              }}
            >
              {t('common.save')}
            </RevxButton>
          </RevxFlexLayout>
        )}
      </Collapse.Panel>
    </Collapse>
  );
};

export default CarriersFormUPS;
